import './styles/ReviewSummary.scss';
import PropTypes from 'prop-types';
import React from 'react';
import ReviewCommentEditor from './ReviewCommentEditor';
import ReviewComments from './ReviewComments';
import ReviewRatings from './ReviewRatings';
import Routes from '../../../../routes';
import requiresReview from './hocs/requiresReview';
import { ReviewContextType, ReviewType } from '../../../propTypes';
import { apiFetch } from 'utils';
import { compose } from 'redux';
import { duckify } from 'utils/duckify';
import { flashMessages } from 'ducks';

// returns my reviews page url
const myReviewsUrl = (school_year_id) => Routes.admissions_reader_reviews_path({ school_year_id, clear: false });

// redirects to next review candidate
const nextCandidate = async ({ contactId, schoolYearId, showFlash }) => {
  try {
    const response = await apiFetch.get({}, `review_contacts?school_year_id=${schoolYearId}`);
    const { review_contacts: contacts } = response;
    const i = contacts.findIndex(c => c.id === contactId);
    const nextContacts = i !== -1 ? [...contacts.slice(i + 1), ...contacts.slice(0, i)] : contacts;
    const nextContact = nextContacts.find(c => !c.review_complete);

    if (nextContact) {
      window.location = `/contacts/${nextContact.id}/reviews?schoolYearId=${schoolYearId}`;
    } else {
      showFlash({ message: 'Great! You finished your list of assigned reviews. Thanks for the hard work!' });
      window.location = myReviewsUrl(schoolYearId);
    }
  } catch (e) {
    showFlash({ type: 'danger', message: 'An error occurred navigating to the next candidate.' });
    throw e;
  }
};

/**
 * Review reader summary page.
 *
 * Shows review comments and ratings, and provides comment editor for summary comment (also called "scores" comment).
 *
 * @param params route params from react-router
 * @param review the review object
 * @param reviewContext the review context
 * @param showFlash function to display a flash message
 * @return {JSX.Element}
 */
const ReviewSummary = ({ params, review, reviewContext, showFlash }) => {
  const { contactId } = params;
  const schoolYearId = review.school_year_id;

  return (
    <div className="review-scores">
      <h2>Your Comments</h2>

      <ReviewComments review={review} reviewContext={reviewContext}/>

      {!review.editable ?
        <React.Fragment>
          <h2>Summary Comments</h2>

          <div className="row">
            <div className="col-24">
              {review.editable && <ReviewCommentEditor
                className="summary-comments"
                review={review}
                commentRef={{ field: 'scores_comment' }}
                placeholder={'Enter your overall comments about this candidate - they\'ll save automatically.'}
              />}

              {!review.editable && review.scores_comment}
            </div>
          </div>
        </React.Fragment>
        : ''
      }

      <h2>Reader Scores</h2>

      <p>
        When you have finished reading, rate the candidate on the following criteria to complete your review.
      </p>

      <ReviewRatings review={review} schoolYearId={schoolYearId}/>

      <div className="row">
        <div className="col-5"/>
        <div className="col-19">
          <div className="review-scores-next-candidate">
            <button className="btn btn-success" onClick={() => nextCandidate({ contactId, schoolYearId, showFlash })}>
              Continue to Next Candidate
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-5"/>
        <div className="col-19">
          <div className="review-scores-back-to-reviews">
            <a className="btn btn-primary" href={myReviewsUrl(schoolYearId)}>
              Back to My Reviews
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

ReviewSummary.propTypes = {
  params: PropTypes.shape({
    contactId: PropTypes.string,
  }).isRequired,
  review: ReviewType.isRequired,
  reviewContext: ReviewContextType.isRequired,
  showFlash: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
});

export default compose(
  requiresReview,
  duckify(() => ({}), () => ({}), mapDispatchToProps)
)(ReviewSummary);
